import React, { createContext, useContext, useEffect, useState } from 'react';
import LanguageService from '../../../shared/LanguageService';
import {
    highlightTranslatedTextAfterRender,
    pulseTranslationKeyAfterRender
} from './highlightTranslatedText';
import { clearHoveredTranslation, keepFocusInsideEditor } from './renderedTranslations';
import { showTranslationKeysInPage } from './translationKeyDisplay';
import { endTranslationPreview, keepTranslationPreview } from './translationPreview';
import { TranslationDto } from '../types';

type TranslationEditorState = {
    editTranslationsEnabled: boolean;
    showTranslationKeys: boolean;
    showNotifications: boolean;
    selectedKey: string | null;
    savedTranslation: TranslationDto | null;
};

type TranslationEditorContextValue = TranslationEditorState & {
    enableEditTranslations: () => void;
    disableEditTranslations: () => void;
    setShowTranslationKeys: (show: boolean) => void;
    setShowNotifications: (show: boolean) => void;
    selectKey: (key: string | null) => void;
    cancelEditing: () => void;
    finishEditing: (saved: TranslationDto) => void;
    dismissSavedNotice: () => void;
};

const TranslationEditorContext = createContext<TranslationEditorContextValue | undefined>(
    undefined
);

export function useTranslationEditor(): TranslationEditorContextValue {
    const context = useContext(TranslationEditorContext);
    if (!context) {
        throw new Error(
            'Translation editor can not be accessed outside TranslationEditorProvider!'
        );
    }
    return context;
}

const initialState: TranslationEditorState = {
    editTranslationsEnabled: false,
    showTranslationKeys: false,
    showNotifications: false,
    selectedKey: null,
    savedTranslation: null
};

function textShownFor(saved: TranslationDto): string | null {
    const loadedLanguage = LanguageService.getLanguage();
    return loadedLanguage ? saved.translations[loadedLanguage.key] || null : null;
}

export function TranslationEditorProvider({ children }: { children: React.ReactNode }) {
    const [state, setState] = useState<TranslationEditorState>(initialState);

    // eslint-disable-next-line no-restricted-syntax -- shielding the panel from the focus traps of open modals is a document listener that needs an effect with cleanup
    useEffect(() => keepFocusInsideEditor(), []);

    function hideTranslationKeys() {
        if (LanguageService.showTranslationKeys) {
            LanguageService.setShowTranslationKeys(false);
            showTranslationKeysInPage(false);
        }
    }

    function enableEditTranslations() {
        setState((previous) => ({ ...previous, editTranslationsEnabled: true }));
    }

    function disableEditTranslations() {
        endTranslationPreview();
        hideTranslationKeys();
        clearHoveredTranslation();
        setState((previous) => ({
            ...previous,
            editTranslationsEnabled: false,
            showTranslationKeys: false,
            showNotifications: false,
            selectedKey: null
        }));
    }

    function setShowTranslationKeys(show: boolean) {
        LanguageService.setShowTranslationKeys(show);
        showTranslationKeysInPage(show);
        setState((previous) => ({ ...previous, showTranslationKeys: show }));
    }

    function setShowNotifications(show: boolean) {
        setState((previous) => ({ ...previous, showNotifications: show }));
    }

    function selectKey(key: string | null) {
        endTranslationPreview();
        setState((previous) => ({ ...previous, selectedKey: key }));
        if (key !== null) {
            pulseTranslationKeyAfterRender(key);
        }
    }

    function cancelEditing() {
        selectKey(null);
    }

    function finishEditing(saved: TranslationDto) {
        keepTranslationPreview();
        hideTranslationKeys();
        clearHoveredTranslation();
        setState((previous) => ({
            ...previous,
            editTranslationsEnabled: false,
            showTranslationKeys: false,
            showNotifications: false,
            selectedKey: null,
            savedTranslation: saved
        }));
        highlightTranslatedTextAfterRender(textShownFor(saved));
    }

    function dismissSavedNotice() {
        setState((previous) => ({ ...previous, savedTranslation: null }));
    }

    return (
        <TranslationEditorContext.Provider
            value={{
                ...state,
                enableEditTranslations,
                disableEditTranslations,
                setShowTranslationKeys,
                setShowNotifications,
                selectKey,
                cancelEditing,
                finishEditing,
                dismissSavedNotice
            }}
        >
            {children}
        </TranslationEditorContext.Provider>
    );
}
